import React, { useState } from "react";
import { Search, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

/**
 * Postcode → address autocomplete using Royal Mail PAF data via our
 * /api/address-lookup/postcode/{postcode} proxy. Customer enters their
 * postcode, clicks "Find address", picks from the dropdown, and the
 * full address auto-fills the checkout form.
 *
 * Props:
 *   onSelect({ address_line1, address_line2, city, postcode, country })
 *   defaultPostcode  — prefill (optional, e.g. from previous abandoned cart)
 */
export default function AddressLookup({ onSelect, defaultPostcode = "" }) {
  const [postcode, setPostcode] = useState(defaultPostcode);
  const [results, setResults] = useState(null); // null | [] | [...addresses]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null);

  const cleanedPostcode = postcode.trim().toUpperCase();

  async function handleFind(e) {
    e?.preventDefault?.();
    setError("");
    setResults(null);
    setPicked(null);
    if (cleanedPostcode.replace(/\s+/g, "").length < 5) {
      setError("Please enter a full UK postcode.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/address-lookup/postcode/${encodeURIComponent(cleanedPostcode)}`,
      );
      const addresses = data?.addresses || [];
      if (addresses.length === 0) {
        setError(
          "No Royal Mail addresses found for that postcode. Please check and try again, or enter your address manually below.",
        );
      } else {
        setResults(addresses);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "";
      if (err?.response?.status === 429) {
        setError("Too many lookups — please slow down and try again.");
      } else if (err?.response?.status === 400) {
        setError("That doesn't look like a valid UK postcode.");
      } else {
        setError(
          msg ||
            "Address lookup is offline — please enter your address manually below.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  function handlePick(addr) {
    setPicked(addr);
    setResults(null);
    onSelect?.(addr);
  }

  return (
    <div
      className="border border-[#00C2D6]/30 bg-[#00C2D6]/[0.04] rounded-sm p-4 sm:p-5 mb-5"
      data-testid="address-lookup-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={14} className="text-[#5EEAD4]" />
        <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]">
          Quick address finder · Royal Mail
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={postcode}
          onChange={(e) => {
            setPostcode(e.target.value);
            setError("");
            // If user edits postcode after a successful lookup, clear stale state
            if (results) setResults(null);
            if (picked) setPicked(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleFind();
            }
          }}
          placeholder="Enter your postcode — e.g. SW1A 1AA"
          autoComplete="postal-code"
          inputMode="text"
          className="pg-input flex-1 uppercase"
          data-testid="address-lookup-postcode"
          aria-label="Postcode for address lookup"
        />
        <button
          type="button"
          onClick={handleFind}
          disabled={loading}
          className="pg-btn-primary px-4 sm:px-6 inline-flex items-center justify-center gap-2 shrink-0"
          data-testid="address-lookup-find-btn"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Searching
            </>
          ) : (
            <>
              <Search size={14} /> Find address
            </>
          )}
        </button>
      </div>

      {error && (
        <div
          className="mt-3 text-xs text-[#FF8A5B] leading-relaxed"
          role="alert"
          data-testid="address-lookup-error"
        >
          {error}
        </div>
      )}

      {results && results.length > 0 && (
        <div className="mt-3" data-testid="address-lookup-results">
          <div className="text-[10px] pg-mono uppercase tracking-[0.18em] text-[#9CA8B8] mb-2">
            {results.length} addresses found — pick yours
          </div>
          <div className="max-h-56 overflow-y-auto border border-[#00C2D6]/25 rounded-sm divide-y divide-[#00C2D6]/15 bg-[#0B1220]/60">
            {results.map((addr) => (
              <button
                key={addr.id}
                type="button"
                onClick={() => handlePick(addr)}
                className="w-full text-left px-3 py-2 text-sm text-[#F7FAFC] hover:bg-[#A6FF00]/10 hover:text-[#A6FF00] transition-colors"
                data-testid={`address-lookup-pick-${addr.id}`}
              >
                {addr.address_line1}
                {addr.address_line2 ? `, ${addr.address_line2}` : ""}
                <span className="text-[#9CA8B8]">
                  {" "}
                  · {addr.city} · {addr.postcode}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {picked && (
        <div
          className="mt-3 flex items-start gap-2 text-xs text-[#A6FF00]"
          data-testid="address-lookup-confirmed"
        >
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
          <div>
            Address filled below — edit any field if needed before paying.
          </div>
        </div>
      )}

      <div className="mt-3 text-[10px] text-[#9CA8B8] leading-relaxed">
        Powered by Royal Mail's Postcode Address File — eliminates typos and
        ensures next-day delivery hits the right door.
      </div>
    </div>
  );
}
